/* Pending:
Required Backend Endpoints

1) GET waiver by ID
GET /business_waiver_builder/waiver_master/:id

2) PATCH step 4
PATCH /business_waiver_builder/waiver_master/smart_step3/:id
*/

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";
import API_URL from "../../api/API";
import WaiverStepLayout from "./WaiverStepLayout";
import WAIVER_STEP_RULES from "./WaiverStepConfig";

const BusinessSmartTemplateStep4 = () => {
  const { business } = useBusinessAuthContext();
  const navigate = useNavigate();
  const { waiverId } = useParams();

  const [returningSignerPolicy, setReturningSignerPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  const [waiver, setWaiver] = useState(null); 
  const stepRules = WAIVER_STEP_RULES["step4"] || {};
  const statusRule = stepRules[waiver?.waiverStatus] || {};
  const isReadOnly = statusRule.readOnly ?? false;
  const readOnlyReason = statusRule.reason ?? "";

  const [saving, setSaving] = useState(false);

  // Load existing waiver data
  useEffect(() => {
    if (!business) {
      navigate("/business/login");
      return;
    }

    const fetchWaiver = async () => {
      try {
        const res = await fetch(
          `${API_URL.BUSINESS_WAIVER_BY_ID}/${waiverId}`,
          {
            headers: {
                Authorization: `Bearer ${business.token}`,
                "x-session-id": business.sessionId,               
                "x-frontend-url": window.location.pathname  
            },
          }
        );

        const data = await res.json();

        if (data.success) {
          const validPolicy = normalizePolicy(data.waiver.returningSignerPolicy);
          setReturningSignerPolicy(validPolicy);
          setWaiver(data.waiver);
        }
      } catch (err) {
        console.error("Failed to load waiver", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWaiver();
  }, [business, waiverId, navigate]);

  const normalizePolicy = (value) => {
    if (!value) return null;

    const v = String(value).trim().toLowerCase();

    if (v === "standard_resign") return "STANDARD_RESIGN";
    if (v === "quick_resign") return "QUICK_RESIGN";

    return null; // anything else is invalid
  };

  const handleContinue = async () => {
    if (!returningSignerPolicy) return;

    if (saving) return;
    
    setSaving(true);

    try {
      const res = await fetch(
        `${API_URL.BUSINESS_WAIVER_SMART_STEP_RETURNING_POLICY}/${waiverId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${business.token}`,
            "x-session-id": business.sessionId,             
            "x-frontend-url": window.location.pathname  
          },
          body: JSON.stringify({
            returningSignerPolicy,
          }),
        }
      );

      const data = await res.json();

      if (data.success) {
        navigate(
          `/business/waiverbuilder/smart/${waiverId}/step-5`
        );
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error("Failed to save step 4", err);
    } finally {
      // If we are still on this page, re-enable button
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
    {/* <div className="h-screen flex bg-background">
      <Sidebar />

      <div className="flex-1 overflow-auto py-10">  */ }
      <WaiverStepLayout  waiver={waiver} currentStep="step-4"  isReadOnly={isReadOnly} readOnlyReason={readOnlyReason} >
        
        <div className="max-w-3xl mx-auto px-6">

          
          <div className="flex items-center justify-between mb-6">
            {/* Back */}
            <button
              onClick={() =>
                navigate(`/business/waiverbuilder/smart/${waiverId}/step-3`)
              }
              className="text-sm text-muted-foreground"
            >
            ← Previous Step
            </button>

            {/* Next (only when readOnly) */}
            {isReadOnly && (
              <button
                onClick={() =>
                  navigate(`/business/waiverbuilder/smart/${waiverId}/step-5`)
                }
                className="text-sm text-muted-foreground"
              >
                Next Step →
              </button>
            )}
          </div>
          
          
          <h1 className="text-2xl font-semibold mb-1">
            Returning Signers
          </h1>

          <p className="text-sm text-muted-foreground mb-8">
            Step 4 of 7 · Configure repeat signing behavior
          </p>

          <div className="bg-white rounded-xl shadow-sm p-8">

            {/* Standard Re-sign */}
            <div
              onClick={() => {
                if (!isReadOnly) {
                  setReturningSignerPolicy("STANDARD_RESIGN");
                }
              }}
              className={`border rounded-lg p-4 mb-4 ${
                isReadOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              } ${
                returningSignerPolicy === "STANDARD_RESIGN"
                  ? "border-yellow-500 bg-yellow-50"
                  : "border-gray-200"
              }`}
            >
              <div className="font-medium">
                Standard Re-Sign (Recommended)
              </div>

              <div className="text-sm text-muted-foreground">
                Returning participants must complete the waiver again in full.
                Ensures updated liability coverage and fresh consent.
              </div>
            </div>

            {/* Quick Re-sign */}
            <div
               onClick={() => {
                if (!isReadOnly) {
                  setReturningSignerPolicy("QUICK_RESIGN");
                }
              }}
              className={`border rounded-lg p-4 ${
                isReadOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              } ${
                returningSignerPolicy === "QUICK_RESIGN"
                  ? "border-yellow-500 bg-yellow-50"
                  : "border-gray-200"
              }`}
            >
              <div className="font-medium">
                Quick Re-Sign
              </div>

              <div className="text-sm text-muted-foreground">
                Returning participants confirm their previous waiver
                quickly without completing all sections again.
                Faster experience but slightly reduced coverage.
              </div>
            </div>

            {!isReadOnly && ( 
              <div className="flex justify-end mt-8">
              <button
                disabled={!returningSignerPolicy || saving}
                onClick={handleContinue}
                className={`px-6 py-3 rounded-full font-medium ${
                  !returningSignerPolicy || saving
                    ? "bg-yellow-100 text-yellow-400 cursor-not-allowed"
                    : "bg-yellow-500 hover:bg-yellow-600 text-black"
                }`}
              >
                {saving ? "Saving..." : "Continue →"}
              </button>
            </div>
            )}
            
          </div>
        </div>
      </WaiverStepLayout>  
    {  /* </div>
    </div> */ }
    </>
  );
};

export default BusinessSmartTemplateStep4;